/* fs-sticky.js — the mobile sticky Add to Cart bar. On a phone it is the ONLY
   variant picker AND the only Add to Cart, so read the readiness gate, the
   rebuild rule, the scroll gate and the sold-out rule before changing anything.

   ============================================================================
   ⚠⚠ THE THEME REPLACES <sticky-atc> ON A VARIANT CHANGE ⚠⚠
   ============================================================================
   Not mutates — REPLACES. Everything we inject into it (the mirror, the Notify
   Me button, the price cell) is destroyed, and a cached `sticky` reference goes
   detached, which reads as an element whose computed styles are all empty.

   This caused the worst bug of the whole build, for about ten minutes on
   2026-09-06: the mirror vanished, so `isUsable()` went false and the bar never
   came back — but `fs-atc-ready` was still set, so the in-page picker and the
   in-page Add to Cart stayed hidden. THE PRODUCT PAGE HAD NO BUY UI AT ALL.

   Three defences, all load-bearing:
     1. `stickyEl()` RE-ACQUIRES the element whenever the cached one is
        disconnected. Never cache `sticky` across an interaction.
     2. `ensure()` rebuilds the injected cells and runs after every change and
        click, plus on a MutationObserver watching the bar's parent.
     3. `fs-atc-ready` is REMOVABLE. If the bar is still unusable 1500ms later,
        the class comes off and the in-page controls return. A brief flash of the
        page's own controls is trivial next to an unbuyable product.

   ============================================================================
   PICKER-AGNOSTIC AND VARIANT-ID DRIVEN
   ============================================================================
   The catalogue renders variant pickers three ways, and before 2026-09-06 this
   bar only worked on one of them — 6 of 33 products silently got no bar:

     data-selector-type="native"    a real <select>; each <option value> IS the
                                    variant id. 27 products.
     data-selector-type="dropdown"  the theme's <custom-select>: a <button> and
                                    a <ul role="listbox"> of
                                    li.js-option[data-variant-id], each with a
                                    <span> label and `is-unavailable` when out
                                    of stock. 4 products (Williams Water).
     no choice at all               a single-variant product. 1 product
                                    (Claire Cap, and any future one-variant item).

   AND THE OPTION IS NOT ALWAYS CALLED "Size" — the e-gift card's is
   "Denominations". The old code hard-coded `[data-option="Size"]`.

   HISTORICAL NOTE, because it reads like a regression and is not: the FIRST
   version of this file read `.js-option[data-value]` — the DROPDOWN markup. It
   was written for the other picker, and the catalogue moved to `native`
   underneath it. Both are supported now.

   ⚠ ONE CELL MEANS ONE CHOICE. `pickerKind()` counts selectors offering MORE
   THAN ONE value. Williams has two — Size with four values, Color with one — so
   one real choice, which we can carry. A product with two genuine multi-value
   options cannot fit an 84px cell, so it returns 'unknown', the bar stays off
   and the page keeps its own controls. THAT is what makes it safe for the CSS
   to hide every `.option-selector` under `html.fs-atc-ready`.

   ⚠ THE CUSTOM-SELECT ONLY LISTENS WHILE IT IS OPEN. `addListboxOpenListeners()`
   attaches the click handler when the listbox opens and drops it on close, so a
   synthetic click on a closed option does NOTHING — silently. Drive it through
   `customSelectEl.selectOption(li)`, which is what its own handler calls. The
   open-then-click fallback covers the day that method is renamed. Either way
   `syncFromPicker()` re-reads the mirror FROM the form, so the bar can never
   claim a variant that is not really selected.

   ---- THE SCROLL GATE -----------------------------------------------------
   Ezra: "on mobile USALs ATC is hidden until you start scrolling, its a nice
   feature no?" Show above 0.18 x innerHeight, hide below 0.08, with hysteresis.

   ⚠ OUR THRESHOLD IS NOT USAL'S ~300px. They keep their in-page picker, so when
   their bar is down there is still something to act on. Ours hides it.

   ⚠ NO rAF THROTTLE on the scroll handler — `if (ticking) return;` + rAF
   deadlocks permanently wherever rAF does not fire.

   ⚠ NO TRANSITION on the reveal. Three transitions in this theme have been
   caught frozen at their start value.

   ---- THE READINESS GATE ---------------------------------------------------
   `html.fs-atc-ready` is set only once the bar is confirmed usable: element,
   product form, hidden variant `id` input, submit button, no unmirrored
   line-item properties, AND either a working mirror or a product with genuinely
   nothing to choose.

   ⚠ THE MIRROR REQUIREMENT IS THE INTERLOCK. If a choice exists and we cannot
   carry it, `isUsable()` must stay false so the in-page controls survive.

   ⚠ IT DOES NOT TRACK SCROLL OR AVAILABILITY. The class is not cleared because
   the bar is scrolled away or the size is sold out — only because the bar has
   actually stopped working.

   ---- THE SOLD-OUT RULE ---------------------------------------------------
   Only the middle cell swaps: Add to Cart out, "Notify me" in, forwarding to the
   back-in-stock app's button in `.nm-portal`. Sold-out options stay selectable
   on purpose — a customer has to be able to land on one to ask to be told.

   ⚠ Notify Me WAITS, it does not scroll. On a variant change the app tears down
   `.nm-portal` and its button is absent for ~3.5 SECONDS.

   ---- NO BACKSLASHES IN THIS FILE -----------------------------------------
   /sold\\s*out/i written here went through the theme file API as a DOUBLE
   backslash and silently never matched. indexOf and charCodeAt instead. */
(function () {
  'use strict';

  var MOBILE_BP = 768;
  var SHOW_AT = 0.18;
  var HIDE_AT = 0.08;
  var UNREADY_GRACE = 1500;

  var sticky = null;
  var scrolledPast = false;
  var notifyWaiting = false;
  var unreadyTimer = null;

  /* ⚠ RE-ACQUIRE, NEVER CACHE ACROSS AN INTERACTION — see the header. */
  function stickyEl() {
    if (!sticky || !sticky.isConnected) sticky = document.querySelector('sticky-atc');
    return sticky;
  }

  function stickyForm() {
    var el = stickyEl();
    return el ? el.querySelector('.js-product-form, form[action*="/cart/add"]') : null;
  }

  /* ---- the product form is the source of truth ------------------------- */

  function productForm() {
    return document.querySelector('.product-form');
  }

  function currentVariantId() {
    var f = productForm();
    var i = f ? f.querySelector('input[name="id"]') : null;
    return i ? String(i.value || '') : '';
  }

  /* ⚠ LINE-ITEM PROPERTIES ARE NOT MIRRORED, SO A PRODUCT THAT USES THEM KEEPS
     ITS OWN FORM. The e-gift card's form carries
     properties[__shopify_send_gift_card_to_recipient], properties[Recipient
     email], [Recipient name], [Message] and [Send on], and its recipient UI is
     visible. The sticky form carries none of them, so a customer could fill the
     whole gift form in, buy from the bar, and have every word of it silently
     dropped — AND THE ADD WOULD SUCCEED, which is what makes this nastier than
     having no bar at all. Found in the 2026-09-06 audit before it shipped.

     This is deliberately a general test, not a gift-card special case: any
     future product with engraving, monogramming or a personal message is caught
     by the same rule.

     Quantity is NOT part of this test. The theme renders the quantity input at
     zero size on this store, so it is always 1 and nothing is lost by the
     sticky form not carrying it. */
  function needsOwnForm() {
    var main = productForm();
    if (!main) return false;
    if (!main.querySelector('[name^="properties["]')) return false;
    var sf = stickyForm();
    return !(sf && sf.querySelector('[name^="properties["]'));
  }

  /* ---- the picker, whichever kind it is -------------------------------- */

  function allPickers() {
    var f = productForm();
    return (f || document).querySelectorAll('.option-selector');
  }

  /* -1 means "present but not a shape we can read" — never treat that as zero. */
  function valueCount(host) {
    var nat = host.querySelector('select');
    if (nat) return nat.options.length;
    var lis = host.querySelectorAll('.js-option[data-variant-id]');
    if (lis.length) return lis.length;
    return -1;
  }

  function pickerHost() {
    var hosts = allPickers();
    var found = null;
    var i, n;
    for (i = 0; i < hosts.length; i++) {
      n = valueCount(hosts[i]);
      if (n > 1) {
        if (found) return null;   /* two real choices — see the header */
        found = hosts[i];
      }
    }
    return found;
  }

  function pickerKind() {
    var hosts = allPickers();
    if (!hosts.length) return 'none';
    var choices = 0;
    var i, n;
    for (i = 0; i < hosts.length; i++) {
      n = valueCount(hosts[i]);
      if (n < 0) return 'unknown';
      if (n > 1) choices++;
    }
    if (choices === 0) return 'none';
    if (choices > 1) return 'unknown';
    var host = pickerHost();
    if (!host) return 'unknown';
    return host.querySelector('select') ? 'native' : 'dropdown';
  }

  function squash(str) {
    var out = [];
    var prev = false;
    var i, ch, code;
    for (i = 0; i < str.length; i++) {
      ch = str.charAt(i);
      code = str.charCodeAt(i);
      if (code <= 32 || code === 160) {
        if (!prev) { out.push(' '); prev = true; }
      } else {
        out.push(ch);
        prev = false;
      }
    }
    return out.join('').trim();
  }

  function looksSoldOut(text) {
    var t = squash(text || '').toLowerCase();
    if (t.indexOf('sold out') > -1) return true;
    if (t.indexOf('soldout') > -1) return true;
    if (t.indexOf('unavailable') > -1) return true;
    return false;
  }

  function readOptions() {
    var host = pickerHost();
    if (!host) return [];
    var out = [];

    var nat = host.querySelector('select');
    if (nat) {
      Array.prototype.forEach.call(nat.options, function (o) {
        var text = squash(o.textContent);
        out.push({ id: String(o.value), label: text, available: !looksSoldOut(text) });
      });
      return out;
    }

    /* The custom-select carries availability as a CLASS and the short label in
       its <span> — keep the <em>Sold Out</em> out of the label. */
    Array.prototype.forEach.call(host.querySelectorAll('.js-option[data-variant-id]'), function (li) {
      var span = li.querySelector('span');
      out.push({
        id: String(li.getAttribute('data-variant-id')),
        label: squash(span ? span.textContent : li.textContent),
        available: !li.classList.contains('is-unavailable')
      });
    });
    return out;
  }

  function soldOutSelected() {
    var id = currentVariantId();
    var opts = readOptions();
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].id === id) return !opts[i].available;
    }
    return false;
  }

  /* Drive the REAL picker so the theme updates price, gallery and both forms
     exactly as it would for a tap. Never write the hidden input directly. */
  function applyVariant(id) {
    if (!id || id === currentVariantId()) return;
    var host = pickerHost();
    if (!host) return;

    var nat = host.querySelector('select');
    if (nat) {
      if (nat.value === id) return;
      nat.value = id;
      nat.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    var li = host.querySelector('.js-option[data-variant-id="' + id + '"]');
    if (!li) return;

    var cs = host.querySelector('custom-select');
    if (cs && typeof cs.selectOption === 'function') {
      try { cs.selectOption(li); } catch (e) {}
    }

    /* Fallback for the day that method is renamed: opening the listbox is what
       makes the component attach its click listener. The in-page picker is
       display:none on mobile, so opening it is invisible. */
    window.setTimeout(function () {
      if (currentVariantId() === id) return;
      var h = pickerHost();
      var target = h && h.querySelector('.js-option[data-variant-id="' + id + '"]');
      if (!target) return;
      var btn = h.querySelector('.custom-select__btn');
      if (btn) btn.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
      target.click();
      window.setTimeout(ensure, 250);
    }, 150);
  }

  /* ---- the injected cells ---------------------------------------------- */

  function mirrorSelect() {
    var el = stickyEl();
    return el ? el.querySelector('.fs-sticky-size__select') : null;
  }

  function buildSizeSelect() {
    var el = stickyEl();
    if (!el || mirrorSelect()) return;
    if (needsOwnForm()) return;
    var kind = pickerKind();
    if (kind !== 'native' && kind !== 'dropdown') return;

    var opts = readOptions();
    if (!opts.length) return;

    var wrap = document.createElement('div');
    wrap.className = 'fs-sticky-size';

    var sel = document.createElement('select');
    sel.className = 'fs-sticky-size__select';
    var host = pickerHost();
    sel.setAttribute('aria-label', 'Select ' + ((host && host.getAttribute('data-option')) || 'option'));

    var current = currentVariantId();
    opts.forEach(function (o) {
      var opt = document.createElement('option');
      opt.value = o.id;
      /* Listed but deliberately NOT disabled. The native picker already writes
         "— Sold out" into its labels; the dropdown does not, so add it there. */
      opt.textContent = (o.available || looksSoldOut(o.label)) ? o.label : o.label + ' — Sold out';
      if (o.id === current) opt.selected = true;
      sel.appendChild(opt);
    });

    wrap.appendChild(sel);

    var buttons = el.querySelector('.sticky-atc__buttons');
    if (buttons) el.insertBefore(wrap, buttons);
    else el.appendChild(wrap);

    sel.addEventListener('change', function () {
      applyVariant(sel.value);
      window.setTimeout(ensure, 60);
      window.setTimeout(ensure, 500);
      window.setTimeout(ensure, 1100);
    });
  }

  function inPageNotifyButton() {
    return document.querySelector('.nm-portal button, .restock-alerts-notify-button');
  }

  function buildNotify() {
    var el = stickyEl();
    if (!el || el.querySelector('.fs-sticky-notify')) return;
    var buttons = el.querySelector('.sticky-atc__buttons');
    if (!buttons || !buttons.parentNode) return;

    var b = document.createElement('button');
    b.type = 'button';
    b.className = 'fs-sticky-notify';
    b.textContent = 'Notify me';

    b.addEventListener('click', function () {
      var real = inPageNotifyButton();
      if (real) { real.click(); return; }
      if (notifyWaiting) return;

      notifyWaiting = true;
      b.style.opacity = '0.6';
      var tries = 0;
      var iv = window.setInterval(function () {
        tries++;
        var r = inPageNotifyButton();
        if (r) {
          window.clearInterval(iv);
          notifyWaiting = false;
          b.style.opacity = '';
          r.click();
          return;
        }
        if (tries > 40) {
          window.clearInterval(iv);
          notifyWaiting = false;
          b.style.opacity = '';
          var row = document.querySelector('.product-form .quantity-submit-row__submit');
          if (row && row.scrollIntoView) row.scrollIntoView({ block: 'center' });
        }
      }, 150);
    });

    buttons.parentNode.insertBefore(b, buttons.nextSibling);
  }

  function buildPrice() {
    var el = stickyEl();
    if (!el || el.querySelector('.fs-sticky-price')) return;
    var src = document.querySelector('.product-info__price .price__current, .price__current');
    if (!src) return;

    var cell = document.createElement('span');
    cell.className = 'fs-sticky-price';
    cell.setAttribute('aria-hidden', 'true');
    cell.textContent = squash(src.textContent);
    el.appendChild(cell);

    try {
      new MutationObserver(function () {
        var t = squash(src.textContent);
        if (t && cell.isConnected && cell.textContent !== t) cell.textContent = t;
      }).observe(src, { childList: true, characterData: true, subtree: true });
    } catch (e) {}
  }

  /* The mirror follows the FORM, never the other way round — so a failed
     applyVariant() corrects itself instead of leaving the bar lying. */
  function syncFromPicker() {
    var mir = mirrorSelect();
    if (!mir) return;
    var id = currentVariantId();
    if (!id || mir.value === id) return;
    for (var i = 0; i < mir.options.length; i++) {
      if (mir.options[i].value === id) { mir.selectedIndex = i; return; }
    }
  }

  /* ---- readiness -------------------------------------------------------- */

  function isUsable() {
    var el = stickyEl();
    if (!el) return false;
    if (needsOwnForm()) return false;
    var form = stickyForm();
    if (!form) return false;
    if (!form.querySelector('input[name="id"]')) return false;
    if (!el.querySelector('.add-to-cart, [name="add"], button[type="submit"]')) return false;

    var kind = pickerKind();
    /* Nothing to choose: a single-variant product can have a bar with no picker
       cell, because there is no choice to take away from the customer. */
    if (kind === 'none') return true;
    return !!mirrorSelect();
  }

  function reveal() {
    var el = stickyEl();
    var usable = isUsable();

    if (usable) {
      document.documentElement.classList.add('fs-atc-ready');
      if (unreadyTimer) { window.clearTimeout(unreadyTimer); unreadyTimer = null; }
    } else if (document.documentElement.classList.contains('fs-atc-ready') && !unreadyTimer) {
      /* ⚠ THE FAIL-SAFE. The bar has stopped working — most likely the theme
         just replaced it mid-variant-change. Give it a beat to be rebuilt, and
         if it still is not usable, hand the page its own controls back rather
         than leaving the product unbuyable. */
      unreadyTimer = window.setTimeout(function () {
        unreadyTimer = null;
        ensure();
        if (!isUsable()) document.documentElement.classList.remove('fs-atc-ready');
      }, UNREADY_GRACE);
    }

    if (!el) return;
    el.classList.toggle('fs-atc-show', usable && scrolledPast);
    el.classList.toggle('fs-atc-soldout', soldOutSelected());
    el.classList.toggle('fs-atc-nosize', usable && !mirrorSelect());
  }

  /* Rebuild anything missing, then re-read state. Every step is idempotent, so
     this is safe to call as often as you like. */
  function ensure() {
    buildSizeSelect();
    buildNotify();
    buildPrice();
    syncFromPicker();
    reveal();
  }

  /* ---- scroll ----------------------------------------------------------- */

  function updateScrolled() {
    var y = window.pageYOffset || document.documentElement.scrollTop || 0;
    var h = window.innerHeight || 812;
    var next = scrolledPast;
    if (!scrolledPast && y > h * SHOW_AT) next = true;
    else if (scrolledPast && y < h * HIDE_AT) next = false;
    if (next === scrolledPast) return false;
    scrolledPast = next;
    return true;
  }

  function onScroll() {
    if (updateScrolled()) reveal();
  }

  /* ---- init ------------------------------------------------------------- */

  function init() {
    if (window.innerWidth >= MOBILE_BP) return;
    var el = stickyEl();
    if (!el) return;

    updateScrolled();
    ensure();

    window.addEventListener('scroll', onScroll, { passive: true });

    /* Capture phase on document, and BOTH events: `change` covers the native
       select, `click` covers the custom-select, which never fires `change`.
       Several passes because the theme re-renders — and REPLACES the bar — a
       beat after the interaction. */
    function onInteract(e) {
      if (!e.target || e.target === mirrorSelect()) return;
      window.setTimeout(ensure, 0);
      window.setTimeout(ensure, 350);
      window.setTimeout(ensure, 900);
    }
    document.addEventListener('change', onInteract, true);
    document.addEventListener('click', onInteract, true);

    /* Catches the replacement itself, including any path that does not come
       from a user interaction. Scoped to the bar's parent, so it is cheap. */
    try {
      var host = el.parentNode;
      if (host && window.MutationObserver) {
        new MutationObserver(function () { window.setTimeout(ensure, 0); })
          .observe(host, { childList: true });
      }
    } catch (e) {}

    function again() { updateScrolled(); ensure(); }
    window.addEventListener('load', again);
    window.addEventListener('pageshow', again);
    window.addEventListener('resize', again, { passive: true });
    window.addEventListener('orientationchange', again, { passive: true });
    window.setTimeout(again, 400);
    window.setTimeout(again, 1400);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();